import React, {Ref, RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import {control, radius, resolveWeight, space, type, universal} from '../../design-system/tokens';
import AccountImg from '../../images/svg/icons/HeaderAccount.svg';

import {ACCOUNT_BANNER_HEIGHT, ACCOUNT_BANNER_WIDTH, AccountBanner} from './AccountBanner';
import {useBand} from './breakpoints';
import {chromeType} from './chromeType';
import {headerChrome} from './headerChrome';
import {heightsFor} from './headerGeometry';
import {menuLinkUnderline, UnderlinePressable} from './MenuLinkText';

export type AccountMenuItem = {label: string; onPress(): void};

export type AccountMenuProps = {
  isLoggedIn: boolean;
  initials?: string;
  /** Greeting name ("Hi John,"); the greeting is omitted without it. */
  firstName?: string;
  /** Panel rows in order. The last one sits below the second divider (Log Out in Figma). */
  items: AccountMenuItem[];
  /** DIS-2043, Figma 470:20040: a guest's account press goes straight to sign-up; no panel. */
  onGuestPress(): void;
  /**
   * The banner is a link that calls it (R21: BrandHeader passes Rewards). Undefined hides the
   * banner (R42: gift-card stores have no Rewards route).
   */
  onBannerPress?(): void;
  /** Controlled open state: DesktopHeader draws the backdrop. Uncontrolled when omitted. */
  open?: boolean;
  onOpenChange?(open: boolean): void;
  /** A pointerdown here is not outside: the backdrop's own press closes the panel. */
  backdropRef?: RefObject<View | null>;
};

// SHORTCUT: radius 2 on chips and nav items is off the radius scale (verdict "design error:
// radius scale is control 4 / card 8"). Upgrade path: design picks radius.control.
export const CHIP_RADIUS = 2;
// SHORTCUT: icon size 24 has no icon-size token (Figma 77:6151, DIS-2043). Upgrade path: a
// DIS-1735 icon-size scale.
export const ACCOUNT_ICON_SIZE = 24;
/**
 * HeaderAccount.svg fills its own 23.7307 × 24 viewBox; Figma's profile-small insets the same
 * drawing 3 in a 24 box. The padded viewBox scales it by 24 / 31.641 to that inset. Each call
 * site repeats the viewBox because the web svgr config strips it (SVGO removeViewBox).
 */
const ACCOUNT_ICON_VIEWBOX = '-3.955 -3.955 31.641 31.641';
// SHORTCUT: avatar 24 has no control-size token (redline account-dropdown token sheet:
// "new token proposal"). Upgrade path: a DIS-1735 avatar size token.
export const AVATAR_SIZE = 24;
// SHORTCUT: panel width 245 (a hug of the 213 content plus 2 × 16 padding) and row gap 18
// (nearest space.s16 / s24) are off-scale; redline verdict "design error". Upgrade path:
// design moves the gap onto the space scale and the width follows from the content.
const PANEL_WIDTH = 245;
const ROW_GAP = 18;
const BANNER_LABEL = 'Personalised member offers';
/** The panel top sits on the bottom edge of the 56 top row (desktop 103:4320, y 56). */
const ROW_HEIGHT = heightsFor('desktop', 'nonEcom').full;

// SHORTCUT: line height 100% (Figma "lh 100%") for greeting, items and initials; lineHeight
// tokens cover 4 styles only. Upgrade path: DIS-1735 adds these to lineHeight.ts.
export const lineHeightOf = (fontSize: number | undefined) => ({lineHeight: fontSize});

/** The panel's band-specific metrics; padding, ground and corners are shared. */
export type AccountPanelMetrics = {
  /** Panel width; the banner spans it less the 2 × space.s16 padding. */
  width: number;
  /** Column gap between the greeting-and-banner group, the dividers and the rows. */
  gap: number;
  /** Gap between the greeting and the banner. */
  introGap: number;
  greeting: TextStyle;
  item: TextStyle;
};

/** Desktop and tablet (Figma 161:17210, 104:8601). */
const DESKTOP_METRICS: AccountPanelMetrics = {
  width: PANEL_WIDTH,
  gap: ROW_GAP,
  introGap: ROW_GAP,
  greeting: {
    ...chromeType.label15Bold,
    ...lineHeightOf(chromeType.label15Bold.fontSize),
    color: headerChrome.brandOnWhite,
  },
  item: {
    ...chromeType.label14Medium,
    ...lineHeightOf(chromeType.label14Medium.fontSize),
    color: universal.black,
  },
};

export type AccountPanelProps = Pick<AccountMenuProps, 'firstName' | 'items' | 'onBannerPress'> & {
  metrics: AccountPanelMetrics;
  /** Called before a row's or the banner's own press. */
  onClose(): void;
  /** Placement only: the caller positions the panel. */
  style?: StyleProp<ViewStyle>;
  /** The panel's host node: on web, the caller's outside-press check reads it. */
  panelRef?: Ref<View>;
};

/**
 * The account panel body: greeting, banner link, divider, rows and a second divider above
 * the last row (Log Out). AccountMenu (desktop-class) and MobileHeader share it.
 */
export const AccountPanel = ({
  firstName,
  items,
  onBannerPress,
  metrics,
  onClose,
  style,
  panelRef,
}: AccountPanelProps) => {
  const head = items.slice(0, -1);
  const tail = items.slice(-1);
  // Figma draws the banner as a scaled instance of the 628 × 192 artwork (desktop ×0.543,
  // 213 × 65.1, redline token-sheet.md; mobile ×0.5): a scale artefact, not a spec. The
  // artwork is scaled to the content width.
  const bannerWidth = metrics.width - 2 * space.s16;
  const bannerScale = bannerWidth / ACCOUNT_BANNER_WIDTH;
  const bannerHeight = ACCOUNT_BANNER_HEIGHT * bannerScale;
  // DIS-1937: rows and banner take the mega menu link hover (UnderlinePressable, DIS-1938).
  const renderItem = (item: AccountMenuItem) => (
    <UnderlinePressable
      key={item.label}
      testID="site-header-account-item"
      accessibilityRole="button"
      accessibilityLabel={item.label}
      onPress={() => {
        onClose();
        item.onPress();
      }}>
      {underlined => (
        <Text style={[metrics.item, underlined && menuLinkUnderline]}>{item.label}</Text>
      )}
    </UnderlinePressable>
  );

  return (
    <View
      ref={panelRef}
      testID="site-header-account-panel"
      style={[styles.panel, {width: metrics.width, gap: metrics.gap}, style]}>
      {firstName || onBannerPress ? (
        <View style={{gap: metrics.introGap}}>
          {firstName ? <Text style={metrics.greeting}>{`Hi ${firstName},`}</Text> : null}
          {onBannerPress ? (
            <UnderlinePressable
              testID="site-header-account-banner"
              style={[styles.banner, {width: bannerWidth, height: bannerHeight}]}
              accessibilityRole="link"
              accessibilityLabel={BANNER_LABEL}
              onPress={() => {
                onClose();
                onBannerPress();
              }}>
              {underlined => (
                // The transform scales the 628 × 192 box about its centre: centred first.
                <View
                  testID="site-header-account-banner-art"
                  style={[
                    styles.bannerArt,
                    {
                      left: (bannerWidth - ACCOUNT_BANNER_WIDTH) / 2,
                      top: (bannerHeight - ACCOUNT_BANNER_HEIGHT) / 2,
                      transform: [{scale: bannerScale}],
                    },
                  ]}>
                  <AccountBanner label={BANNER_LABEL} underlined={underlined} />
                </View>
              )}
            </UnderlinePressable>
          ) : null}
        </View>
      ) : null}
      <View testID="site-header-account-divider" style={styles.divider} />
      {head.map(renderItem)}
      {tail.length > 0 ? (
        <>
          <View testID="site-header-account-divider" style={styles.divider} />
          {tail.map(renderItem)}
        </>
      ) : null}
    </View>
  );
};

/** The logged-in account glyph: the initials on a white circle (desktop 24, mobile 28). */
export const AccountAvatar = ({
  initials,
  size,
  testID,
}: {
  initials: string;
  size: number;
  testID: string;
}) => (
  <View
    testID={testID}
    style={[styles.avatar, {width: size, height: size, borderRadius: size / 2}]}>
    <Text style={styles.initials}>{initials}</Text>
  </View>
);

/**
 * The account chip and its dropdown (Figma 161:17210 desktop, 104:8601 tablet). Logged out it
 * opens no panel and calls `onGuestPress` (DIS-2043). Data-driven: Task A10 supplies the item
 * targets.
 *
 * Disclosure semantics, not a menu (R43): the chip is a button with `aria-expanded`, and the
 * panel holds plain buttons and a link in tab order. A `menu` role would promise arrow-key
 * navigation this component does not implement. On web, Escape closes the panel and returns
 * focus to the chip.
 */
export const AccountMenu = ({
  isLoggedIn,
  initials,
  firstName,
  items,
  onGuestPress,
  onBannerPress,
  open,
  onOpenChange,
  backdropRef,
  panelAlign = 'centre',
}: AccountMenuProps & {
  /**
   * 'centre' (DIS-1937): the panel is centred under the chip. 'end': its right edge is the
   * chip's. DesktopHeader passes 'end' when no cart chip follows: centred, the panel would run
   * 40.5 px past the viewport (measured at 1024 / 1280 / 1440, logged out, no store).
   */
  panelAlign?: 'centre' | 'end';
}) => {
  const [ownOpen, setOwnOpen] = useState(false);
  const rootRef = useRef<View>(null);
  const chipRef = useRef<View>(null);
  const tablet = useBand() === 'tablet';
  const shown = open ?? ownOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      setOwnOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  // A sign-in, a logout or a session expiry closes the panel: its rows change.
  useEffect(() => setOpen(false), [isLoggedIn, setOpen]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !shown) {
      return;
    }
    const onKeyDown = (event: {key?: string}) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // On web the ref is the DOM node; `focus` moves keyboard focus back to the chip.
        (chipRef.current as unknown as {focus?: () => void} | null)?.focus?.();
      }
    };
    // On web the View ref is the DOM node, so `contains` tells inside from outside.
    const onPointerDown = (event: {target?: unknown}) => {
      type Node = {contains?: (n: unknown) => boolean} | null | undefined;
      const nodes = [rootRef.current, backdropRef?.current] as unknown as Node[];
      if (!nodes.some(node => node?.contains?.(event.target))) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [shown, setOpen, backdropRef]);

  const onChipPress = () => (isLoggedIn ? setOpen(!shown) : onGuestPress());

  return (
    <View ref={rootRef}>
      <Pressable
        ref={chipRef}
        testID="site-header-account-chip"
        onPress={onChipPress}
        style={styles.chip}
        accessibilityRole="button"
        accessibilityLabel="Account"
        // aria-expanded, not accessibilityState: react-native-web 0.21 drops the latter.
        aria-expanded={isLoggedIn ? shown : undefined}>
        {isLoggedIn && initials ? (
          <AccountAvatar
            initials={initials}
            size={AVATAR_SIZE}
            testID="site-header-account-avatar"
          />
        ) : (
          // Same SVG as release/12.x BrandHeader (DIS-1842), not the FA6 `user` glyph.
          <AccountImg
            testID="site-header-account-icon"
            viewBox={ACCOUNT_ICON_VIEWBOX}
            width={ACCOUNT_ICON_SIZE}
            height={ACCOUNT_ICON_SIZE}
            color={headerChrome.onGround}
            aria-hidden={true}
          />
        )}
      </Pressable>
      {shown ? (
        <AccountPanel
          firstName={firstName}
          items={items}
          onBannerPress={onBannerPress}
          metrics={DESKTOP_METRICS}
          onClose={() => setOpen(false)}
          style={[
            styles.panelPlacement,
            panelAlign === 'end' ? styles.panelEnd : styles.panelCentred,
            tablet && styles.panelTablet,
          ]}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s4,
    paddingHorizontal: space.s8,
    paddingVertical: space.s4,
    borderRadius: CHIP_RADIUS,
  },
  avatar: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: universal.white,
  },
  initials: {
    fontSize: type.paragraph.p3.fontSize,
    ...resolveWeight('bold'),
    ...lineHeightOf(type.paragraph.p3.fontSize),
    color: headerChrome.brandOnWhite,
  },
  // Figma tablet 104:6397 sits at y 60 and 6 px past the cart chip; one placement is used on
  // both bands (redline: "design error (two placements)").
  panelPlacement: {
    position: 'absolute',
    top: '50%',
    marginTop: ROW_HEIGHT / 2,
    zIndex: 1,
  },
  // DIS-1937: centred under the chip (Jira design). With the cart chip next, the panel's right
  // edge lands 0.6 px past the cart chip's (measured in Storybook at 1024 / 1280 / 1440).
  panelCentred: {left: '50%', marginLeft: -PANEL_WIDTH / 2},
  // No cart chip: the account chip ends the row, so the panel ends at the chip's right edge.
  panelEnd: {right: 0},
  panel: {
    paddingVertical: space.s24,
    paddingHorizontal: space.s16,
    backgroundColor: universal.white,
    borderBottomLeftRadius: radius.card,
    borderBottomRightRadius: radius.card,
  },
  // Tablet 104:6397 rounds all four corners; desktop 103:4320 the bottom two only.
  panelTablet: {borderTopLeftRadius: radius.card, borderTopRightRadius: radius.card},
  banner: {overflow: 'hidden'},
  bannerArt: {position: 'absolute'},
  divider: {height: control.borderWidth, backgroundColor: universal.greyMedium},
});
